import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import { RootState } from '../../redux/store'
import PointBar from '../../components/userAdmin/PointBar'
import CourseTile from '../../components/userAdmin/CourseTile'
import { Button, PageTitle, SkipLink } from '../../components/atoms'
import Footer from '../../components/userAdmin/Footer'
import BoltLeftIcon from '../../assets/images/bolt-left.png'
import BoltRightIcon from '../../assets/images/bolt-right.png'
import apiServices from '../../libs/services'
import { Attempt, Module } from '../../models/global.types'
import { useAuth } from '../../hooks/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { setFirstActiveModule, setModules } from '../../redux/slices/moduleSlice'
import { setCurrentAttempt } from '../../redux/slices/quizSlice'
import config from '../../config/config'
import StudentMenu from '../../components/organisms/Menu/StudentMenu'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { useBackgroundMusic } from '../../contexts/BackgroundMusicContext'
import { getBgmUrl, getSfxUrl } from '../../utils/audio'
import { BgmTrack, SfxTrack } from '../../enums/enums'
import { hasUrlFeatureFlag } from '../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { defaultPageTitle } from '../../components/atoms/PageTitle/PageTitle'
import menuIcon from '../../assets/images/menu-icon.png'
import menuIconPlaceholder from '../../assets/images/menu-icon-placeholder.png'
import ProgressiveImage from '../../components/molecules/ProgressiveImage/ProgressiveImage'
import { logger } from '../../utils'

// Keep these imports as they will used for Redux refactor:
/*
import { useGetCurrentAttemptQuery } from '../../redux/services/quiz'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
*/

export enum FocusableElement {
  ButtonLightningRound = 'button-lightning-round',
  ButtonPlayerPack = 'button-player-pack'
}

export const idUserDashboardContainer = 'user-dashboard-container'
export const idUserDashboardMainContent = 'user-dashboard-main-content'

const UserDashboard: React.FC = () => {
  const [experiencePoints, setExperiencePoints] = useState<number>(0)
  const navigate = useNavigate()
  const [filteredModules, setFilteredModules] = useState<Module[]>([])
  const [currentSession, setCurrentSession] = useState<any | null>(null)
  const { currentAttempt } = useSelector((state: RootState) => state.quiz)
  const { modules } = useSelector((state: RootState) => state.module)
  const { state } = useLocation()
  const { userProfile } = useAuth()
  const keyboardControls = useKeyboardControls()
  const dispatch = useDispatch()
  const DASHBOARD_INSTRUCTIONS_VIDEO = `${config.loadEnvironmentVariables().courseFilesUrl}video/dashboard-instructions.mp4`

  const bgMusic = useBackgroundMusic()
  const bgmTrackNormal = getBgmUrl(BgmTrack.Dashboard)
  const bgmTrackCompleted = getBgmUrl(BgmTrack.Completed)
  const sfxLevelEnterUrl = getSfxUrl(SfxTrack.LevelEnter)
  const userPreferences = useUserPreferences()

  /**
   * Will attempt to get the attempts and sessions for the user.
   * If the user has no attempts, it will create a new one.
   */
  const fetchAttemptsAndSessions = useCallback(async () => {
    const response = await apiServices.getCurrentAttemptAndSession()
    const currentAttempt = response?.data?.data?.attempt
    const currentSession = response?.data?.data?.session
    dispatch(setCurrentAttempt(currentAttempt))

    setExperiencePoints((currentAttempt?.totalScore != null) ? currentAttempt.totalScore : 0)

    // NOTE: sessions are confusing and do not make sense.  These will go away eventually in place of using
    // only attempts. Session data is meant to track user browser sessions and will be replaced with telemetry
    // tools.
    setCurrentSession(currentSession)
  }, [dispatch])

  const fetchModules = useCallback(async () => {
    if (!Array.isArray(modules) || modules.length <= 0) {
      const res = await apiServices.fetchModules()
      const modulesArr = res?.data?.data?.modules
      const m = modulesArr.filter(
        (module: Module) => `${module.name}`.toLowerCase() !== 'lightning').sort((a: any, b: any) => a.sort - b.sort)
      dispatch(setModules(m))
    }
  }, [dispatch, modules])

  useEffect(() => {
    if (modules !== null && modules !== undefined && modules.length > 0) {
      // get first active module
      const firstActiveModule = modules.find((module) => {
        return module.isActive
      })

      if (firstActiveModule !== undefined) {
        dispatch(setFirstActiveModule(firstActiveModule?.name))
      }

      const updatedModules = modules.map((module) => {
        if (module.id === currentSession?.moduleId) {
          return { ...module, isCurrent: true } // Create a new object
        }
        return { ...module, isCurrent: false } // Ensure all others are set to false
      })

      const currentModule = updatedModules.find((module: Module) => module.isCurrent)

      const finalModules = updatedModules.map((module) => {
        if (currentModule !== undefined) {
          // Find the positions in the sorted module array
          const currentModuleIndex = updatedModules.findIndex(m => m.id === currentModule.id)
          const moduleIndex = updatedModules.findIndex(m => m.id === module.id)

          if (moduleIndex < currentModuleIndex || !module.isActive) {
            module.isCompleted = true
            module.isLocked = false
            module.isCurrent = false
          } else if (moduleIndex > currentModuleIndex) {
            module.isCompleted = false
            module.isLocked = true
            module.isCurrent = false
          }
        } else {
          module.isCompleted = false
          module.isLocked = true
          module.isCurrent = false
        }

        if (currentSession?.name === 'Lightning') {
          module.isCompleted = true
          module.isLocked = false
          module.isCurrent = false
        }

        return module
      })
      setFilteredModules(finalModules)
    }
  }, [currentSession?.moduleId, currentSession?.name, dispatch, modules])

  /**
   * Attempts data required to load the page and set up the user to start taking courses.
   * TODO: refactor with Redux flow.
   */
  useEffect(() => {
    void fetchAttemptsAndSessions()
    void fetchModules()
  }, [fetchAttemptsAndSessions, fetchModules])

  // Manage BG music - wait for preferences to load before playing
  useEffect(() => {
    // Don't play music until preferences are loaded
    if (!userPreferences.isPreferencesLoaded) {
      return
    }

    let url: string
    if (currentAttempt?.status === 'completed') {
      url = bgmTrackCompleted
    } else {
      url = bgmTrackNormal
    }

    bgMusic.play(url)
    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, bgmTrackNormal, bgmTrackCompleted, currentAttempt?.status, userPreferences.isPreferencesLoaded])

  useEffect(() => {
    const walkthroughCompleted = userProfile?.user_profile.walkthrough_completed
    const profileUpdated = state?.profileUpdated
    logger.log('Dashboard walkthrough check', { walkthrough_completed: walkthroughCompleted, profileUpdated })
    if (walkthroughCompleted !== true && profileUpdated !== true) {
      logger.log('Redirecting to walkthrough video')
      navigate('/user-dashboard/instructions', { state: { videoPath: DASHBOARD_INSTRUCTIONS_VIDEO, returnPath: '/user-dashboard' } })
    }
  }, [DASHBOARD_INSTRUCTIONS_VIDEO, userProfile, navigate, state?.profileUpdated])

  useEffect(() => {
    if (userProfile?.user_profile.accepted_terms !== true) {
      navigate('/terms-of-use')
    }
  }, [navigate, userProfile?.user_profile])

  const handleCourseClick = useCallback(async (module: Module) => {
    if (hasUrlFeatureFlag()) {
      bgMusic.playSoundFx(sfxLevelEnterUrl)
    }

    if (currentSession?.status === 'pending') {
      await apiServices.startNextSession(currentSession?.attemptId)
    }

    if (currentAttempt?.status === 'completed' || module.isCompleted) {
      navigate(`/user-dashboard/module-replay?module=${String(module.name)}`)
    } else {
      navigate(`/user-dashboard/courses/${String(currentSession.id)}`)
    }
  }, [currentSession, currentAttempt?.status, navigate, bgMusic, sfxLevelEnterUrl])

  return (
    <UserDashboardContainer
      currentAttempt={currentAttempt}
      experiencePoints={experiencePoints}
    >
      <div className='flex justify-center' role='region'>
        {
          filteredModules.map((module: Module, index: number) => (
            <CourseTile
              key={`Level ${index + 1}: ${module.name}`}
              loading={modules === null || modules === undefined || currentSession === null || currentSession === undefined}
              label={`Level ${index + 1}: ${module.name}`}
              module={module}
              onClick={() => { void handleCourseClick(module) }}
              reviewState={currentAttempt?.status === 'completed'} // If the user has completed their attempt, all courses should be in the review state.
            />
          ))
        }
      </div>

      {
        currentSession?.name === 'Lightning' && currentAttempt?.status !== 'completed'
          ? (
            <div className='flex justify-center pb-5' role='region'>
              <ControlHighlight
                active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonLightningRound)}
                highlightEffect={HighlightEffect.ButtonHexOutline}
                data-testid={`highlight-${FocusableElement.ButtonLightningRound}`}
                className='max-w-[670px] w-full mt-8 glow-effect'
              >
                <KeyboardControl>
                  <Button
                    id={FocusableElement.ButtonLightningRound}
                    className='h-[120px] w-full font-bold shadow-gray-500 diamond-btn'
                    onClick={() => { void handleCourseClick(currentSession) }}
                  >
                    <div className='flex justify-center items-center'>
                      <img src={BoltLeftIcon} alt='' />
                      <span
                        className='uppercase text-[38px] text-neutral-090 font-bold px-4'
                        aria-label='Launch Level: Lightning Round'
                      >Lightning Round
                      </span>
                      <img src={BoltRightIcon} alt='' />
                    </div>
                  </Button>
                </KeyboardControl>
              </ControlHighlight>
            </div>
            )
          : (
              null
            )
      }

    </UserDashboardContainer>
  )
}

interface UserDashboardContainerProps {
  children: React.ReactNode
  currentAttempt?: Attempt | null
  experiencePoints?: number
}

const UserDashboardContainer: React.FC<UserDashboardContainerProps> = ({ children, currentAttempt = null, experiencePoints }) => {
  const [pageTitle] = useState<string>(defaultPageTitle)
  const navigate = useNavigate()
  const keyboardControls = useKeyboardControls()

  return (
    <div
      id={idUserDashboardContainer}
      data-testid={idUserDashboardContainer}
      className='font-primary min-h-[100dvh] p-4 flex flex-col justify-between'
      style={{ // Have to use style directly for tailwind radial gradient:
        backgroundImage: 'radial-gradient(circle at top, #2C2C2C, #090909)'
      }}
    >
      <RemoveScrollBar />
      <SkipLink targetId={idUserDashboardMainContent} />
      <header className='pb-6'>
        <StudentMenu />
        <PointBar isCompleted={currentAttempt?.status === 'completed'} totalExperiencePoints={experiencePoints} absolutePositioning={false} />
      </header>
      <main
        id={idUserDashboardMainContent}
        data-testid={idUserDashboardMainContent}
        tabIndex={-1}
      >
        <PageTitle title={pageTitle} />
        {children}
        <div
          id='player-pack-button-container'
          className='absolute flex bottom-[24px] right-[16px]'
        >
          <ControlHighlight
            active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonPlayerPack)}
            highlightEffect={HighlightEffect.ButtonHexOutline}
            data-testid={`highlight-${FocusableElement.ButtonPlayerPack}`}
          >
            <KeyboardControl>
              <button
                id={FocusableElement.ButtonPlayerPack}
                data-testid={FocusableElement.ButtonPlayerPack}
                className='px-[17px] py-[14px] flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold hover:bg-[rgba(52,232,158,0.2)]'
                onClick={() => navigate('setting')}
              >
                <ProgressiveImage
                  alt='Open Player Pack'
                  placeholderSrc={menuIconPlaceholder}
                  src={menuIcon}
                  className='w-[52px]'
                />
              </button>
            </KeyboardControl>
          </ControlHighlight>
        </div>
      </main>
      <div className='self-end w-full pt-2'>
        <Footer />
      </div>
    </div>
  )
}

export default UserDashboard
